import theme from '../../../theme';

// Route-status accent colors, keyed by the DriverStatus tiers from
// composeDriverRecords. Superset map: consumers that only deal with active
// tiers fall back with `|| theme.colors.statusNeutral`.
export const STATUS_ACCENT = {
  'on-track': theme.colors.statusOnTrack,
  behind: theme.colors.statusBehind,
  'off-track': theme.colors.statusOffTrack,
  'not-started': theme.colors.statusNeutral,
  completed: theme.colors.statusNeutral,
};

// Tier labels for active routes only (map marker + popup). Deliberately has
// no 'completed'/'not-started' keys — those surfaces fall back to '' or the
// raw status string, matching production output.
export const STATUS_TIER_LABEL = {
  'on-track': 'On-track',
  behind: 'Behind',
  'off-track': 'Off-track',
};

// Card lifecycle labels: the three active tiers all read as "Active" — the
// card surfaces lifecycle, not lateness (that's the ETA badge's job).
export const LIFECYCLE_LABEL = {
  'on-track': 'Active',
  behind: 'Active',
  'off-track': 'Active',
  'not-started': 'Not started',
  completed: 'Completed',
};

// Keyed by the stop's DISPLAY state (stopDisplayState in composeDriverRecords):
// the composer's done/current/pending plus 'skipped' for a pending stop the
// driver has moved past.
export const STOP_STATE_LABEL = {
  done: 'Stocked',
  current: 'In progress',
  pending: 'Pending',
  skipped: 'Skipped',
};

// bg/fg tag + dot tones per stop state (StopPopupContent tag, stop-list
// timeline dots).
export const STOP_STATE_TONE = {
  done: { bg: theme.colors.statusOnTrackBg, fg: theme.colors.statusOnTrack },
  current: { bg: theme.colors.statusBehindBg, fg: theme.colors.statusBehind },
  pending: { bg: theme.colors.bgMuted, fg: theme.colors.textMuted },
  skipped: {
    bg: theme.colors.statusOffTrackBg,
    fg: theme.colors.statusOffTrack,
  },
};

// The disc a stop is drawn with, per display state, on the single-route map
// (mapIcons.makeRouteStopIcon) AND in the Stops panel rows — one table so the
// two never disagree. Stocked stops are the only green; everything not
// stocked yet is ink, filled once the driver is working it and hollow while
// still owed; a pending stop the driver already moved past reads soft red.
export const STOP_DISC_FILL = {
  done: {
    disc: theme.colors.stopStocked,
    text: theme.colors.white,
    hollow: false,
  },
  current: {
    disc: theme.colors.markerInk,
    text: theme.colors.white,
    hollow: false,
  },
  pending: {
    disc: theme.colors.markerInk,
    text: theme.colors.markerInk,
    hollow: true,
  },
  skipped: {
    disc: theme.colors.statusOffTrackBg,
    text: theme.colors.statusOffTrack,
    hollow: false,
  },
};

// `& .<className>.<state>` rules painting an inline disc like the map's stop
// marker: filled discs get the fill color, hollow ones a white face with the
// fill as a ring.
export function discClassVariants(className) {
  const variants = {};
  Object.entries(STOP_DISC_FILL).forEach(([state, fill]) => {
    variants[`& .${className}.${state}`] = fill.hollow
      ? {
          background: theme.colors.white,
          color: fill.text,
          border: `2px solid ${fill.disc}`,
        }
      : { background: fill.disc, color: fill.text };
  });
  return variants;
}

export const OUT_OF_SEQUENCE_TONE = {
  bg: theme.colors.statusOutOfSequenceBg,
  fg: theme.colors.statusOutOfSequence,
};

// List-view row tone per route status: green tint for routes running to plan
// (or already done), light red for late ones, none for routes that haven't
// started — including a missed start, which the list leaves uncolored.
export const ROW_TONE_BY_STATUS = {
  'on-track': 'on-time',
  completed: 'on-time',
  behind: 'late',
  'off-track': 'late',
  'not-started': null,
};

// Delta badge tones keyed by TimelineRow.deltaTier.
export const DELTA_TIER_STYLE = {
  early: { bg: theme.colors.statusOnTrackBg, fg: theme.colors.statusOnTrack },
  'on-time': {
    bg: theme.colors.statusOnTrackBg,
    fg: theme.colors.statusOnTrack,
  },
  over: { bg: theme.colors.statusBehindBg, fg: theme.colors.statusBehind },
  late: { bg: theme.colors.statusOffTrackBg, fg: theme.colors.statusOffTrack },
  eta: { bg: theme.colors.bgMuted, fg: theme.colors.textMuted },
};

// Render a {key: {bg, fg}} tone map as Emotion class variants
// (`& .className.key` selectors), so status-driven colors come from classes
// instead of per-element inline styles. Spread the result into a
// styledComponent block next to the base `.className` entry.
export function toneClassVariants(className, toneMap) {
  const variants = {};
  Object.entries(toneMap).forEach(([key, tone]) => {
    variants[`& .${className}.${key}`] = {
      background: tone.bg,
      color: tone.fg,
    };
  });
  return variants;
}

// Same idea for the flat STATUS_ACCENT map: one CSS property per status key
// (e.g. accent text color, dot background).
export function accentClassVariants(className, cssProp) {
  const variants = {};
  Object.entries(STATUS_ACCENT).forEach(([key, color]) => {
    variants[`& .${className}.${key}`] = { [cssProp]: color };
  });
  return variants;
}
